import React, { useEffect, useState } from "react";
import { TableColumn, TableRow, ToxnotReactTable } from '../ToxnotReactTable';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../ToxnotReactModal';
import { TopActionButtons } from '../TopActionsAndScrollableContentContainer';
import TextInput from "../TextInput";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";

interface SmelterListProps {
    value: string;
    localizer: any;
    onValueChange: (value: Smelter[]) => void;
}

interface CountryData {
    [code: string]: string;
};

interface SmelterList {
    Smelter: Smelter[]
};

interface Smelter {
    City?: string;
    Comments?: string;
    ContactEmail?: string;
    ContactName?: string;
    Country?: string;
    DoesAllFeedstockComeFromScrapOrRecycled?: string;
    Id: string;
    IdNumber?: string;
    IdSource?: string;
    Metal?: string;
    MetalSource?: string;
    MineCountry?: string;
    MineName?: string;
    NextSteps?: string;
    ReferenceName?: string;
    State?: string;
    StreetAddress?: string;
}

const SmelterList: React.FC<SmelterListProps> = ({
    value,
    localizer,
    onValueChange,
}) => {
    const toxnotServiceClient = new ToxnotServiceClient();
    const emptySmelter = JSON.parse('{"Metal":"","ReferenceName":"","Country":"","Id":"","IdNumber":"","IdSource":"","MetalSource":"","StreetAddress":"","City":"","State":"","ContactName":"","ContactEmail":"","NextSteps":"","MineName":"","MineCountry":"","DoesAllFeedstockComeFromScrapOrRecycled":"","Comments":""}');
    const smelterFieldKeys: string[] = [
        "IdNumber", "Metal", "ReferenceName", "StandardName", "Country","Id", "IdSource",
        "StreetAddress", "City", "State", "ContactName", "ContactEmail", "NextSteps", "MetalSource", "MineName", "MineCountry",
        "DoesAllFeedstockComeFromScrapOrRecycled", "Comments"
    ];
    const [smelter, setSmelter] = useState<Smelter>(emptySmelter);
    const [smelterIdsToDelete, setSmelterIdsToDelete] = useState({});
    const [tableItemId, setTableItemId] = useState<string>("");
    const [smelters, setSmelters] = useState<Smelter[]>([]);
    const [countriesWithCodes, setCountriesWithCodes] = useState<CountryData>();

    useEffect(() => {
        let isMounted = true; // Flag to track if component is mounted
        
        const getCountries = async () => {
            const result: any = await toxnotServiceClient.FetchCountries() as any;
            // Check if component is still mounted AFTER the async call completes
            if (isMounted) {
                const countryData: CountryData = result?.data as CountryData;
                setCountriesWithCodes(countryData);
            }
        };
        
        getCountries();
        
        // Parse smelters data
        const parsedSmelters = JSON.parse(value && value.trim() !== "" ? value : "[]");
        if (isMounted) {
            setSmelters(parsedSmelters ?? []);
        }
        
        // Cleanup function that runs when component unmounts
        return () => {
            isMounted = false; // Update flag when component unmounts
        };
    }, [value]);

    
    const getCountryNameByCode = (code: string): string => {
        return localizer.localizeString(countriesWithCodes ? countriesWithCodes[code.toUpperCase()] || "Unknown Country" : "Unknown Country");
    };

    const getSmelterListHeaderRow = (): TableColumn<Smelter>[] => {
        return [
            {
                value: localizer.localizeString("Smelter ID")
            },
            {
                value: localizer.localizeString("Metal")
            },
            {
                value: localizer.localizeString("Smelter Name")
            },
            {
                value: localizer.localizeString("Smelter Country")
            },
            {
                value: localizer.localizeString("Source")
            }
        ];
    }

    const getSmelterListRow = (smelter: Smelter, index: number): TableRow => {
        return {
            cols: [
                smelter.IdNumber ?? localizer.localizeString("Unknown Smelter ID"),
                smelter.Metal ?? localizer.localizeString("Unknown Metal"),
                smelter.ReferenceName ?? localizer.localizeString("Unknown Smelter Name"),
                getCountryNameByCode(smelter.Country ?? ""),
                smelter.IdSource ?? localizer.localizeString("Unknown Source")
            ]
        } as TableRow;
    }

    const handleSmelterRowClick = (e: React.MouseEvent, smelter: Smelter): void => {
        const smelterKey = `${smelter.IdNumber}-${smelter.Metal ?? ""}-${smelter.Country ?? ""}`;
        
        setSmelter(smelter);
        setTableItemId(smelterKey);
    
        $('#smelter-details-modal-v2').modal('show');
    };
    

    const onTableInputValueChange = (fieldName: string, newValue: string, type: string) => {
        const updatedSmelter = { ...smelter };
        updatedSmelter[fieldName as keyof typeof updatedSmelter] = newValue;
        setSmelter(updatedSmelter);
    };

    const renderSmelterDetailsTableHeaderRow = () => {
        return (
            <thead>
                <tr id="headerrow">
                    <th>
                        {localizer.localizeString("Smelter Detail")}
                    </th>
                    <th>
                        {localizer.localizeString("Value")}
                    </th>
                </tr>
            </thead>
        )
    }

    const renderSmelterDetailsTableBody = () => {
        return (
            <tbody>
                {smelterFieldKeys.map(fieldKey => {
                    let propValue = (smelter as any)[fieldKey];

                    if (propValue === null || propValue === undefined) {
                        propValue = "";
                    } else if (typeof propValue !== "string") {
                        propValue = "Unknown";
                    }

                    return (
                        <tr key={fieldKey}>
                            <td><b>{localizer.localizeString(fieldKey === "IdNumber" ? `${fieldKey} (*)` : fieldKey)}</b></td>
                            <td><TextInput value={fieldKey === "Country" ? getCountryNameByCode(propValue) : propValue}
                                onValueChange={(newValue) => onTableInputValueChange(fieldKey, newValue, "Smelter")}
                                additionalClass={fieldKey === 'IdNumber' && propValue === '' ? 'highlighted-input' : ''} />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        )
    }

    const createSmelterButton_click = () => {
        setSmelter(emptySmelter);
        $('#smelter-details-modal-v2').modal('show');
    }

    const onModalCancel = () => {
        setSmelter(emptySmelter);
    }

    const onModalSave = () => {
        if (!smelter.IdNumber) return;
    
        setSmelters((prevSmelters) => {
            let updatedSmelters = [...prevSmelters];
    
            const previousKey = tableItemId;
    
            const existingIndex = updatedSmelters.findIndex(s => 
                `${s.IdNumber}-${s.Metal ?? ""}-${s.Country ?? ""}` === previousKey
            );
    
            if (existingIndex > -1) {
                updatedSmelters[existingIndex] = smelter;
            } else {
                updatedSmelters.push(smelter);
            }
    
            onValueChange(updatedSmelters);
    
            return updatedSmelters;
        });

        setSmelter(emptySmelter);
        setTableItemId("");
        $('#smelter-details-modal-v2').modal('hide');
    };
    
    

    const getSmelterId = (model: Smelter) => {
        return model.IdNumber ?? "Unknown Smelter ID";
    }

    const updateSelectedSmelters = (selectedIds: { [id: string]: string }) => {
        setSmelterIdsToDelete({ ...selectedIds });
    }

    const deleteSmelterButton_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        if (Object.keys(smelterIdsToDelete).length === 0) {
            alert(localizer.localizeString("You must select one or more smelters to delete."));
            return;
        }

        $('#warn-about-deleting-smelters-modal-v2').modal('show');
    }

    const closeDeleteSmeltersWarningModal_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#warn-about-deleting-smelters-modal-v2').modal('hide');
    }

    const getSmelters = () => {
        return smelters;
    }

    const deleteSmeltersButtonConfirm_click = () => {
        setSmelters((prevSmelters) => {
            const selectedIds = new Set(Object.keys(smelterIdsToDelete));
            const updatedSmelters = prevSmelters.filter(s => !selectedIds.has(s.IdNumber??""));
            onValueChange(updatedSmelters);
    
            return updatedSmelters;
        });
        setSmelterIdsToDelete({});
        $('#warn-about-deleting-smelters-modal-v2').modal('hide');
    };
    
    return (
        <div>
            <h4><strong>Smelter List</strong></h4>
            <TopActionButtons>
                <div>
                    <button className="btn btn-default" onClick={createSmelterButton_click}>
                        <span className="glyphicon glyphicon-plus" /> {localizer.localizeString("Create Smelter")}
                    </button>
                    <button className="btn btn-default" onClick={deleteSmelterButton_click}>
                        <span className="glyphicon glyphicon-remove" /> {localizer.localizeString("Delete Smelter")}
                    </button>
                </div>
            </TopActionButtons>
            <div id="smelter-list-table" className="tn-section-padding-container">
                <ToxnotReactTable
                    allRowModels={getSmelters()}
                    localizer={localizer}
                    headerRow={getSmelterListHeaderRow()}
                    getRowForTable={getSmelterListRow}
                    rowClickEventHandler={handleSmelterRowClick}
                    selectingRowOptions={{
                        rowModelGetId: getSmelterId,
                        onRowSelected: updateSelectedSmelters
                    }}
                    enablePaging={true}
                    fullSectionWidth={true}
                />
            </div>

            <ToxnotReactModal modalId={"smelter-details-modal-v2"} localizedTitle="Smelter Details">
                <ToxnotReactModalBody>
                    <table id="createTableSmelter-v2" className="table table-bordered">
                        {renderSmelterDetailsTableHeaderRow()}
                        {renderSmelterDetailsTableBody()}
                    </table>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} onCancel={onModalCancel}>
                    <button type="button" className="btn btn-toxnot-primary"
                        data-dismiss="modal"
                        onClick={() => onModalSave()}
                        disabled={!smelter.IdNumber?.length}>
                        {localizer.localizeString("Save")}
                    </button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>

            <ToxnotReactModal modalId={"warn-about-deleting-smelters-modal-v2"} localizedTitle={localizer.localizeString("Are you sure you want to delete?")}>
                <ToxnotReactModalBody>
                    <div className="alert alert-warning">
                        <p>{localizer.localizeString("You are about to delete smelter(s). This will remove them from the Exchange.")}</p>
                        <p>{localizer.localizeString("Are you sure you want to continue?")}</p>
                    </div>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} enabledCloseButton={false}>
                    <button className="btn btn-default" onClick={closeDeleteSmeltersWarningModal_click}>{localizer.localizeString("Cancel")}</button>
                    <button className="btn btn-danger" onClick={deleteSmeltersButtonConfirm_click}>{localizer.localizeString("Yes, Delete")}</button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>
        </div>
    );
};

export default SmelterList;